import { useEffect, useState } from 'react'
import { PublicQuizAPI, AssessmentSummary } from '../../apis/PublicQuizAPI'
import bg1 from '../../images/bg-1.png'
import { qcls, qfonts } from './quizTheme'

interface Props {
  name: string
  email: string
  role: string
  onStart: (attemptId: string, isResuming: boolean, assessmentName: string, questionCap: number | null) => void
  onBack: () => void
}

export default function SelectQuizPage({ name, email, role, onStart, onBack }: Props) {
  const [assessments, setAssessments] = useState<AssessmentSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [starting, setStarting] = useState<string | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    PublicQuizAPI.listAssessments()
      .then(setAssessments)
      .catch(() => setError('Could not load assessments. Please try again.'))
      .finally(() => setLoading(false))
  }, [])

  const handleSelect = async (slug: string, assessmentName: string) => {
    setError('')
    setStarting(slug)
    const firstName = name.trim().split(/\s+/)[0] ?? ''
    const lastName = name.trim().split(/\s+/).slice(1).join(' ')
    try {
      const [result, info] = await Promise.all([
        PublicQuizAPI.start({ name, firstName, lastName, email, role: role || undefined, assessmentSlug: slug }),
        PublicQuizAPI.fetchInfo(slug).catch(() => ({ questionCap: null })),
      ])
      onStart(result.attemptId, result.isResuming, assessmentName, info.questionCap ?? null)
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Something went wrong. Please try again.')
      setStarting(null)
    }
  }

  return (
    <div className="min-h-screen md:h-screen md:overflow-hidden relative flex flex-col md:flex-row">
      <img
        src={bg1}
        alt=""
        aria-hidden="true"
        className="absolute left-0 top-0 md:top-1/2 md:-translate-y-1/2 w-full md:w-[60%] h-auto object-contain pointer-events-none select-none"
      />

      <div className="relative z-10 md:w-5/12 flex-shrink-0 flex flex-col justify-center items-center pt-16 md:pt-0 px-10 md:px-10">
        <div className="pb-10 px-4 md:px-0">
          <h1 className={`text-[32px] font-black text-black leading-[1.1] mb-6 ${qfonts.heading}`}>
            Choose your <span className="text-black">assessment</span>
          </h1>
          <p className={`text-black/70 text-sm leading-relaxed max-w-[22rem] mb-12 ${qfonts.body}`}>
            Pick the test that best matches what you want to measure. Each option gives you a focused report for a different context.
          </p>

          <div className="space-y-4">
            {[
              'Choose the assessment that fits your context',
              'Each test gives you a focused report',
              'You can return and start a different test anytime',
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 bg-[#61d6ad] border border-black rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className={`text-black/90 text-sm ${qfonts.supporting}`}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="relative z-10 flex-1 flex items-center justify-center px-10 py-14">
        <div className="w-full max-w-[560px] rounded-lg shadow-2xl p-10" style={{ backgroundColor: 'rgba(245, 245, 247, 0.55)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}>
          <div className="mb-8">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-sm text-black hover:text-black transition-colors mb-6"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
              </svg>
              Back
            </button>
            <h2 className="text-[32px] font-normal text-black mb-2 font-quiz-heading">Choose your test</h2>
            <p className="text-black text-sm">
              Hi <span className="font-semibold text-black">{name.split(' ')[0]}</span> — what do you want to see a report on today?
            </p>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-16">
              <svg className={`${qcls.spinner} w-8 h-8 text-brand-yellow`} fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          )}

          {!loading && assessments.length === 0 && !error && (
            <p className="text-gray-500 text-sm">No assessments are currently available.</p>
          )}

          {error && (
            <div className={qcls.errorBox}>
              <svg className="w-[22px] h-[22px] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              {error}
            </div>
          )}

          {!loading && assessments.length > 0 && (
            <ul className="space-y-3">
              {assessments.map((a) => {
                const isStarting = starting === a.slug
                return (
                  <li key={a.slug}>
                    <button
                      disabled={starting !== null}
                      onClick={() => handleSelect(a.slug, a.name)}
                      className={[
                        'w-full text-left px-5 py-4 border transition-all',
                        'hover:border-gray-300 hover:bg-gray-50',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                        isStarting ? 'border-brand-yellow bg-brand-yellow/10' : 'border-gray-200 bg-white/90',
                      ].join(' ')}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-black text-sm">{a.name}</p>
                          {a.shortDescription && (
                            <p className="text-gray-700 text-sm mt-1.5">{a.shortDescription}</p>
                          )}
                        </div>
                        {isStarting ? (
                          <svg className={`${qcls.spinner} w-5 h-5 text-black flex-shrink-0`} fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        ) : (
                          <svg className="w-6 h-6 text-black flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        )}
                      </div>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
